import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Crosshair, Map } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useT } from "@/lib/i18n";
import { api, errMsg, getPosition, fmtTime } from "@/lib/api";
import { Card, Loader, PageTitle } from "@/components/kv";

export default function Settings() {
  const { t } = useT();
  const [f, setF] = useState(null);
  const [attempts, setAttempts] = useState([]);

  useEffect(() => {
    api.get("/settings").then((r) => setF(r.data)).catch((e) => toast.error(errMsg(e, t)));
    api.get("/admin/location-attempts").then((r) => setAttempts(r.data)).catch(() => {});
  }, [t]);

  const applyAttempt = (a) => {
    setF((s) => ({ ...s, cafe_lat: a.lat.toFixed(6), cafe_lng: a.lng.toFixed(6) }));
    toast.info(t("settings.applied"));
  };

  const set = (k, num) => (e) => setF((s) => ({ ...s, [k]: num ? e.target.value.replace(",", ".") : e.target.value }));

  const save = async () => {
    try {
      const body = { ...f, cafe_lat: Number(f.cafe_lat), cafe_lng: Number(f.cafe_lng), radius_m: Number(f.radius_m), late_tolerance_min: Number(f.late_tolerance_min) };
      const { data } = await api.put("/admin/settings", body);
      setF(data);
      toast.success(t("common.saved"));
    } catch (e) {
      toast.error(errMsg(e, t));
    }
  };

  const useMine = async () => {
    try {
      const p = await getPosition();
      setF((s) => ({ ...s, cafe_lat: p.lat.toFixed(6), cafe_lng: p.lng.toFixed(6) }));
      toast.info(`${t("settings.applied")} (±${p.accuracy} m)`);
    } catch (e) {
      toast.error(t(`clock.geo_${e.geo || "unavailable"}`));
    }
  };

  if (!f) return <Loader />;
  return (
    <div>
      <PageTitle kicker={t("adminnav.settings")} title={t("settings.title")} />
      <Card className="p-5 sm:p-7 max-w-2xl space-y-5" data-testid="settings-form">
        <div className="space-y-1"><Label>{t("settings.address")}</Label><Input value={f.address} onChange={set("address")} className="bg-white" data-testid="settings-address-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label>{t("settings.lat")}</Label><Input inputMode="decimal" value={f.cafe_lat} onChange={set("cafe_lat", true)} className="bg-white font-mono-kv" data-testid="settings-lat-input" /></div>
          <div className="space-y-1"><Label>{t("settings.lng")}</Label><Input inputMode="decimal" value={f.cafe_lng} onChange={set("cafe_lng", true)} className="bg-white font-mono-kv" data-testid="settings-lng-input" /></div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" onClick={useMine} data-testid="settings-use-location-button"><Crosshair className="h-4 w-4 mr-1.5" />{t("settings.use_mine")}</Button>
          <a href={`https://www.google.com/maps?q=${f.cafe_lat},${f.cafe_lng}`} target="_blank" rel="noopener noreferrer" data-testid="settings-map-link"
            className="inline-flex items-center rounded-full border border-[#E8DFD0] bg-white px-4 py-2 text-sm hover:border-[#c86d51]"><Map className="h-4 w-4 mr-1.5" />{t("settings.map")}</a>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1"><Label>{t("settings.radius")}</Label><Input type="number" min={10} max={1000} value={f.radius_m} onChange={set("radius_m")} className="bg-white" data-testid="settings-radius-input" /></div>
          <div className="space-y-1"><Label>{t("settings.tolerance")}</Label><Input type="number" min={0} max={120} value={f.late_tolerance_min} onChange={set("late_tolerance_min")} className="bg-white" data-testid="settings-tolerance-input" /></div>
        </div>
        <div className="flex items-start justify-between gap-4 rounded-xl bg-[#FAF6F0] p-4">
          <div>
            <div className="font-semibold">{t("settings.block")}</div>
            <p className="text-sm text-stone-500 mt-1">{t("settings.block_desc")}</p>
          </div>
          <Switch checked={f.block_outside} onCheckedChange={(v) => setF((s) => ({ ...s, block_outside: v }))} data-testid="settings-block-switch" />
        </div>
        <Button className="rounded-full h-11 px-8" onClick={save} data-testid="settings-save-button">{t("common.save")}</Button>
      </Card>
      <Card className="p-5 sm:p-7 max-w-2xl mt-6" data-testid="location-attempts">
        <h2 className="font-display text-2xl font-semibold">{t("settings.attempts")}</h2>
        <p className="text-sm text-stone-500 mt-1 mb-4">{t("settings.attempts_hint")}</p>
        {attempts.length === 0 && <p className="text-sm text-stone-500">{t("settings.attempts_empty")}</p>}
        <div className="space-y-2">
          {attempts.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center gap-3 rounded-xl bg-[#FAF6F0] p-3" data-testid={`attempt-${a.id}`}>
              <div className="flex-1 min-w-[180px]">
                <div className="font-semibold text-sm">{a.name} <span className="font-mono-kv text-xs text-stone-500">{fmtTime(a.at)}</span>
                  {a.suspicious && <span className="ml-2 rounded-full bg-red-100 text-red-800 px-2 py-0.5 text-[11px] font-semibold" data-testid={`attempt-suspicious-${a.id}`}>{t("settings.suspicious")}</span>}
                </div>
                <div className="font-mono-kv text-xs text-stone-600">{a.lat.toFixed(6)}, {a.lng.toFixed(6)}</div>
                <div className="text-xs text-stone-600">{t("settings.distance")}: {a.distance_m} m · {t("settings.accuracy")}: ±{a.accuracy ?? "?"} m</div>
              </div>
              <a href={`https://www.google.com/maps?q=${a.lat},${a.lng}`} target="_blank" rel="noopener noreferrer" className="text-xs underline text-stone-600">{t("settings.map")}</a>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => applyAttempt(a)} data-testid={`attempt-use-${a.id}`}>{t("settings.use_this")}</Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
